import { trackStructEvent } from "@snowplow/browser-tracker";
import { useEffect } from "react";

import { Day1EventAction } from "@/types/snowplow-events";

/**
 * Records which (category|label) SECTION-TOGGLED events have already fired this
 * page load, so a component that remounts — IdentityCheck swaps IDV subtrees on
 * every useUserProgress poll/refetch — does not double-log. Resets on full
 * reload, matching loggedFlags in FeatureFlagsContext. (V2362133998)
 */
const emittedSectionToggles = new Set<string>();

/** @visibleForTesting — clears the dedup set so tests can assert re-emission. */
export const __resetSectionToggleDedup = (): void =>
  emittedSectionToggles.clear();

/**
 * Emit a SECTION-TOGGLED at most once per (category, label) per page load.
 *
 * Use this from inline emitters (e.g. IVVCompleted's success effect) that
 * cannot use the hook form.
 */
export const trackSectionToggledOnce = (
  category: string,
  label?: string
): void => {
  const key = `${category}|${label ?? ""}`;
  if (emittedSectionToggles.has(key)) return;
  emittedSectionToggles.add(key);
  trackStructEvent({
    category,
    action: Day1EventAction.SectionToggled,
    ...(label && { label }),
  });
};

/**
 * Emits a SECTION-TOGGLED Snowplow event for the given category on mount,
 * at most once per (category, label) per page load.
 *
 * Use this instead of a bare `useEffect(() => trackStructEvent(...), [])` in
 * step components. Components that remount (IdentityCheck swaps IDV subtrees
 * on useUserProgress polls) will not re-emit. (V2362133998)
 */
export const useTrackSectionToggled = (
  category: string,
  label?: string
): void => {
  useEffect(() => {
    trackSectionToggledOnce(category, label);
  }, []);
};
